/**
 * Rótulos e badges de status/recomendação do recommendation-engine.
 *
 * AF-3418: estes mapas estavam duplicados em RecommendationEngineRecommendationsList,
 * RecommendationEngineParecerView e ProviderDocumentsGrid, cada um com um conjunto
 * diferente de chaves. Centralizados aqui para a tradução ser consistente entre as telas.
 *
 * A normalização é importante: a API devolve o status em maiúsculas ("PROCESSING"), mas a
 * tela de recomendações exibia o valor cru e aparecia "Processing" para o usuário.
 */

const BADGE_BASE = 'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium';

export type BatchStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'ERROR';

const BATCH_STATUS_LABELS: Record<string, string> = {
    PENDING: 'Pendente',
    PROCESSING: 'Processando',
    COMPLETED: 'Concluído',
    ERROR: 'Erro',
    FAILED: 'Falhou',
    CANCELED: 'Cancelado',
    CANCELLED: 'Cancelado',
};

const BATCH_STATUS_BADGE: Record<string, string> = {
    PENDING: `${BADGE_BASE} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`,
    PROCESSING: `${BADGE_BASE} bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200`,
    COMPLETED: `${BADGE_BASE} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`,
    ERROR: `${BADGE_BASE} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`,
    FAILED: `${BADGE_BASE} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`,
    CANCELED: `${BADGE_BASE} bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-gray-200`,
    CANCELLED: `${BADGE_BASE} bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-gray-200`,
};

/** Normaliza o status para a chave canônica (maiúsculas, sem espaços/hífens). */
export const normalizeStatus = (status?: string | null): string =>
    (status ?? '').trim().toUpperCase().replace(/[\s-]+/g, '_');

export const getBatchStatusLabel = (status?: string | null): string => {
    const key = normalizeStatus(status);
    if (!key) return 'N/A';
    // Sem tradução conhecida: devolve capitalizado em vez do valor cru em caixa alta
    return BATCH_STATUS_LABELS[key] ?? key.charAt(0) + key.slice(1).toLowerCase();
};

export const getBatchStatusBadgeClass = (status?: string | null): string => {
    const key = normalizeStatus(status);
    return (
        BATCH_STATUS_BADGE[key] ??
        `${BADGE_BASE} bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-gray-200`
    );
};

/** Recomendação normalizada usada nas telas (inclui ERROR e NO_DATA). */
export type RecommendationState =
    | 'APPROVED'
    | 'REJECTED'
    | 'PENDING'
    | 'ERROR'
    | 'NO_DATA'
    | 'PROCESSING';

const RECOMMENDATION_LABELS: Record<string, string> = {
    APPROVED: 'Aprovado',
    REJECTED: 'Reprovado',
    PENDING: 'Pendente',
    ERROR: 'Erro na análise',
    // AF-3460: o bureau respondeu, mas não havia dado para o documento. Nada falhou,
    // então o texto não sugere reprocessamento.
    NO_DATA: 'Sem dados',
    PROCESSING: 'Processando',
};

const RECOMMENDATION_BADGE: Record<string, string> = {
    APPROVED: `${BADGE_BASE} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`,
    REJECTED: `${BADGE_BASE} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`,
    PENDING: `${BADGE_BASE} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`,
    // Laranja com borda: visualmente distinto de "reprovado", porque aqui não houve decisão
    ERROR: `${BADGE_BASE} bg-orange-100 text-orange-900 border border-orange-400 dark:bg-orange-900 dark:text-orange-100 dark:border-orange-500`,
    // AF-3460: cinza neutro, deliberadamente sem a carga de alerta do ERROR. Não houve
    // falha nem violação — só não havia o que avaliar.
    NO_DATA: `${BADGE_BASE} bg-gray-100 text-gray-700 border border-gray-300 dark:bg-slate-700 dark:text-gray-200 dark:border-slate-500`,
    PROCESSING: `${BADGE_BASE} bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-200`,
};

/**
 * AF-3460: aceita tanto o estado normalizado da tela ('NO_DATA') quanto o valor cru do
 * backend ('SEM_DADOS').
 *
 * Antes só o estado normalizado era reconhecido, então passar o valor do motor caía no
 * fallback e um documento sem dados era rotulado "Erro na análise" — exatamente a confusão
 * que esta task remove. Normalizar aqui elimina a armadilha para qualquer chamador.
 */
export const getRecommendationLabel = (recommendation?: string | null): string => {
    if (!recommendation) return RECOMMENDATION_LABELS.ERROR;
    return RECOMMENDATION_LABELS[normalizeRecommendation(recommendation)];
};

export const getRecommendationBadgeClass = (recommendation?: string | null): string => {
    if (!recommendation) return RECOMMENDATION_BADGE.ERROR;
    return RECOMMENDATION_BADGE[normalizeRecommendation(recommendation)];
};

/**
 * Traduz o valor cru do motor de regras (português) para o estado usado nas telas.
 *
 * AF-3418: o default é ERROR, não PENDING. Valor ausente ou desconhecido significa que a
 * análise não chegou — nunca deve ser exibido como aprovado ou como pendência de decisão.
 */
export const normalizeRecommendation = (raw?: string | null): RecommendationState => {
    const key = normalizeStatus(raw);
    switch (key) {
        case 'APROVADO':
        case 'APPROVED':
            return 'APPROVED';
        case 'REPROVADO':
        case 'REJEITADO':
        case 'REJECTED':
            return 'REJECTED';
        case 'PENDENTE':
        case 'PENDING':
            return 'PENDING';
        case 'SINALIZAR_CRÉDITO':
        case 'SINALIZAR_CREDITO':
        case 'SINALIZADO_CREDITO':
            // Sinalização de crédito não é reprovação; a coluna própria é dirigida
            // por credit_flag, à parte da recomendação.
            return 'APPROVED';
        case 'SEM_DADOS':
        case 'NO_DATA':
            return 'NO_DATA';
        case 'PROCESSING':
        case 'PROCESSANDO':
            return 'PROCESSING';
        default:
            return 'ERROR';
    }
};
